import React from 'react';
import { Modal, Form, Input, Select, Button, Upload, message, Spin, AutoComplete, Checkbox, Dropdown } from 'antd';
import {
    UploadOutlined,
    DownOutlined,
    CloseOutlined,
    ArrowLeftOutlined,
    FileTextOutlined,
    CloseCircleFilled,
    InfoCircleFilled,
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    AlignLeftOutlined,
    AlignCenterOutlined,
    AlignRightOutlined,
    MenuOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import EFaxRecipientQueryInput from 'components/form/EFaxRecipientQueryInput';
import type { FaxContact, FaxCoverPageTemplateVo, CoverPageResponse, TFaxContactListQuery } from 'types/eFax';
import { FAX_TAG_OPTIONS } from 'types/eFax';
import { faxSend, faxCoverPageTemplateList, faxContactCreate, faxDocumoList, faxContactList } from 'api/fax';
import {
    isFaxScheduleSuccess,
    findFaxContactByNumber,
    formatFaxNumber,
    faxNumbersLooselyEqual,
    extractFaxContactListPage,
    mapFaxContactVoToFaxContact,
    isFaxContactServerId,
    normalizeFaxForApi,
    recipientFaxForSendApi,
    faxNanpDigitsForInput,
} from 'utils/eFaxScheduleRows';
import { useDebounceFn, useRequest } from 'ahooks';
import { getProspectList } from 'api/prospect';
import type { Prospect } from 'types/common';
import useGetDocument from 'hooks/useGetDocument';
import DocumentsTable from 'pages/dashboard/ProspectsDetailPage/components/DocumentTab/components/DocumentsTable';
import type { IGetDocumentItem } from 'types/document';
import s from './s.module.less';

/** Open “Send fax” from patient Documents with chart PDFs already picked (source=DOCUMENT, documentIds set). */
export type SendFaxInitialAttachment = {
    clientId: number;
    documents: IGetDocumentItem[];
};

/** Optional overrides for POST /fax/send (e.g. INTAKE + sourceId + intakeBusinessType, documentIds null). */
export type SendFaxSendContext = {
    source?: string;
    sourceId?: number;
    intakeBusinessType?: string | null;
    /** When source is INTAKE, prefill and send with this chart client id */
    clientId?: number;
};

interface Props {
    open: boolean;
    onClose: () => void;
    contacts: FaxContact[];
    /** Bump Sent Fax tab list after a successful send */
    onSendSuccess?: () => void;
    /** Refresh dashboard contact list (e.g. after “Save as contact” from send flow) */
    onContactsChanged?: () => void;
    /** Prefill patient + attachments when launching from client Documents tab */
    initialAttachment?: SendFaxInitialAttachment | null;
    /** When set, overrides inferred source / sourceId / intakeBusinessType on send */
    faxSendContext?: SendFaxSendContext | null;
}

const isPdfDocument = (item: IGetDocumentItem) => {
    const name = (item.fileOriginName || item.name || '').toLowerCase();
    const type = (item.fileType || item.documentType || '').toLowerCase();
    return name.endsWith('.pdf') || type.includes('pdf');
};

const validateAndAddFile = (file: File, onAdd: (uf: UploadFile) => void): boolean | typeof Upload.LIST_IGNORE => {
    if (file.type !== 'application/pdf') {
        message.error('Only PDF files are supported');
        return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 >= 100) {
        message.error('File must be smaller than 100MB');
        return Upload.LIST_IGNORE;
    }
    onAdd({
        uid: file.uid || `${Date.now()}`,
        name: file.name,
        status: 'done',
        originFileObj: file as unknown as UploadFile['originFileObj'],
    });
    return false;
};

/** Merge dashboard contacts + API search hits for recipient autocomplete (Figma: search or manual fax). */
function mergeRecipientContactSuggestions(
    query: string,
    propsContacts: FaxContact[],
    apiHits: FaxContact[],
    max = 50,
): FaxContact[] {
    const q = query.trim().toLowerCase();
    const qDigits = query.replace(/\D/g, '');

    const matchesQuery = (c: FaxContact) => {
        if (!q) return true;
        const name = (c.name || '').toLowerCase();
        const faxStr = `${c.faxNumberDisplay || ''} ${c.faxNumber || ''}`.toLowerCase();
        const faxDigits = (c.faxNumber || '').replace(/\D/g, '');
        if (name.includes(q) || faxStr.includes(q)) return true;
        if (qDigits.length >= 2 && faxDigits.includes(qDigits)) return true;
        return false;
    };

    if (!q) {
        return [...propsContacts]
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
            .slice(0, Math.min(30, max));
    }

    const seen = new Set<string>();
    const out: FaxContact[] = [];
    const keyOf = (c: FaxContact) => (c.faxNumber || '').replace(/\D/g, '') || c.id;

    const add = (c: FaxContact) => {
        const k = keyOf(c);
        if (!k || seen.has(k)) return;
        seen.add(k);
        out.push(c);
    };

    propsContacts.filter(matchesQuery).forEach(add);
    apiHits.filter(matchesQuery).forEach(add);
    return out.slice(0, max);
}

function shouldFetchContactSearch(trimmed: string): boolean {
    if (trimmed.length < 1) return false;
    const digitsOnly = trimmed.replace(/\D/g, '');
    const letterish = /[a-zA-Z]/.test(trimmed);
    if (letterish && trimmed.length >= 2) return true;
    if (digitsOnly.length >= 3) return true;
    return false;
}

// ─── Rich text area component ────────────────────────────────────────────────

interface RichTextAreaProps {
    value?: string;
    onChange?: (v: string) => void;
    placeholder?: string;
}

const RichTextArea: React.FC<RichTextAreaProps> = ({ value, onChange, placeholder }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const prevValueRef = React.useRef<string | undefined>(value);

    React.useEffect(() => {
        if (ref.current && value !== prevValueRef.current) {
            prevValueRef.current = value;
            if (document.activeElement !== ref.current) {
                ref.current.innerHTML = value || '';
            }
        }
    }, [value]);

    const exec = (cmd: string) => {
        ref.current?.focus();
        document.execCommand(cmd, false);
        onChange?.(ref.current?.innerHTML || '');
    };

    return (
        <div className={s.richTextWrapper}>
            <div className={s.richTextToolbar}>
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('bold');
                    }}
                >
                    <BoldOutlined />
                </button>
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('italic');
                    }}
                >
                    <ItalicOutlined />
                </button>
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('underline');
                    }}
                >
                    <UnderlineOutlined />
                </button>
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('strikeThrough');
                    }}
                >
                    <StrikethroughOutlined />
                </button>
                <div className={s.richTextToolbarDivider} />
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('justifyLeft');
                    }}
                >
                    <AlignLeftOutlined />
                </button>
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('justifyCenter');
                    }}
                >
                    <AlignCenterOutlined />
                </button>
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('justifyRight');
                    }}
                >
                    <AlignRightOutlined />
                </button>
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('justifyFull');
                    }}
                >
                    <MenuOutlined />
                </button>
                <div className={s.richTextToolbarDivider} />
                <button
                    type="button"
                    className={s.richTextToolbarBtn}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        exec('insertUnorderedList');
                    }}
                >
                    <UnorderedListOutlined />
                </button>
            </div>
            <div
                ref={ref}
                contentEditable
                className={s.richTextContent}
                onInput={() => onChange?.(ref.current?.innerHTML || '')}
                data-placeholder={placeholder}
                suppressContentEditableWarning
            />
        </div>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────

const SendFaxModal: React.FC<Props> = ({
    open,
    onClose,
    contacts,
    onSendSuccess,
    onContactsChanged,
    initialAttachment = null,
    faxSendContext = null,
}) => {
    const [form] = Form.useForm();
    const patientId = Form.useWatch('patientId', form);
    const recipientValue = Form.useWatch('recipient', form);
    const includeCoverSheet = Form.useWatch('includeCoverSheet', form);

    const [faxStep, setFaxStep] = React.useState<1 | 2>(1);
    const [fileList, setFileList] = React.useState<UploadFile[]>([]);
    const [attachmentMode, setAttachmentMode] = React.useState<'files' | 'documents'>('files');
    const [attachedDocuments, setAttachedDocuments] = React.useState<IGetDocumentItem[]>([]);
    const [sending, setSending] = React.useState(false);
    const [showInfo, setShowInfo] = React.useState(true);
    const [showDocumentsModal, setShowDocumentsModal] = React.useState(false);
    const [selectedDocuments, setSelectedDocuments] = React.useState<IGetDocumentItem[]>([]);

    // Step 2 cover sheet state
    const [coverTemplates, setCoverTemplates] = React.useState<FaxCoverPageTemplateVo[]>([]);
    const [coverTemplatesLoading, setCoverTemplatesLoading] = React.useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | null>(null);
    const [selectedDocumoUuid, setSelectedDocumoUuid] = React.useState<string | null>(null);
    const [buildFromScratch, setBuildFromScratch] = React.useState(false);
    const [documoTemplates, setDocumoTemplates] = React.useState<CoverPageResponse[]>([]);

    const uploadRef = React.useRef<any>(null);

    const [recipientSearchHits, setRecipientSearchHits] = React.useState<FaxContact[]>([]);
    const [recipientSearchLoading, setRecipientSearchLoading] = React.useState(false);
    const [selectedContactMeta, setSelectedContactMeta] = React.useState<{ id: number; fax: string } | null>(null);
    const contactSearchReqId = React.useRef(0);

    const {
        data,
        loading: prospectListLoading,
        run: fetchProspectList,
    } = useRequest(getProspectList, {
        manual: true,
    });
    const [, hasDocuments, documentsLoading] = useGetDocument(patientId, Boolean(patientId));

    const isIntakeFaxSource = faxSendContext?.source === 'INTAKE';

    React.useEffect(() => {
        if (open) {
            fetchProspectList();
            setFaxStep(1);
            setShowInfo(true);
        } else {
            setRecipientSearchHits([]);
            setSelectedContactMeta(null);
        }
    }, [open, fetchProspectList]);

    /** Intake drawer: PDF is generated server-side from sourceId; prefill patient and clear file pickers */
    React.useEffect(() => {
        if (!open || !isIntakeFaxSource) return;
        setFileList([]);
        setAttachedDocuments([]);
        setAttachmentMode('files');
        const cid = faxSendContext?.clientId;
        if (cid != null && Number.isFinite(Number(cid))) {
            form.setFieldsValue({ patientId: Number(cid) });
        }
    }, [open, isIntakeFaxSource, faxSendContext?.clientId, form]);

    /** Documents tab: pre-select client and PDF row(s); fax/send uses source DOCUMENT + documentIds. */
    React.useEffect(() => {
        if (!open || !initialAttachment?.clientId || !initialAttachment.documents?.length) return;
        const pdfDocs = initialAttachment.documents.filter(isPdfDocument);
        if (!pdfDocs.length) {
            message.error('Only PDF documents can be sent by fax.');
            return;
        }
        form.setFieldsValue({ patientId: initialAttachment.clientId });
        setAttachmentMode('documents');
        setAttachedDocuments(pdfDocs);
        setFileList([]);
    }, [open, initialAttachment, form]);

    const { run: runRecipientContactSearch } = useDebounceFn(
        async (q: string) => {
            if (!open) return;
            const trimmed = q.trim();
            if (!shouldFetchContactSearch(trimmed)) {
                setRecipientSearchHits([]);
                setRecipientSearchLoading(false);
                return;
            }
            const reqId = ++contactSearchReqId.current;
            setRecipientSearchLoading(true);
            try {
                const digitsOnly = trimmed.replace(/\D/g, '');
                const useNumber =
                    digitsOnly.length >= 3 && digitsOnly.length >= trimmed.replace(/\s/g, '').length * 0.55;
                const params: TFaxContactListQuery = { current: 1, size: 50 };
                if (useNumber) params.number = digitsOnly;
                else params.name = trimmed;
                const result = await faxContactList(params);
                if (reqId !== contactSearchReqId.current) return;
                const body = result?.data;
                if (!result?.error && body && isFaxScheduleSuccess(body)) {
                    const { records } = extractFaxContactListPage(body);
                    setRecipientSearchHits(records.map(mapFaxContactVoToFaxContact));
                } else {
                    setRecipientSearchHits([]);
                }
            } finally {
                if (reqId === contactSearchReqId.current) {
                    setRecipientSearchLoading(false);
                }
            }
        },
        { wait: 300 },
    );

    React.useEffect(() => {
        if (!open) return;
        runRecipientContactSearch(recipientValue || '');
    }, [recipientValue, open, runRecipientContactSearch]);

    React.useEffect(() => {
        if (!selectedContactMeta) return;
        if (!faxNumbersLooselyEqual(selectedContactMeta.fax, recipientValue || '')) {
            setSelectedContactMeta(null);
        }
    }, [recipientValue, selectedContactMeta]);

    const prospectList: Prospect[] = (data?.data?.data as Prospect[] | undefined) || [];
    const clientOptions = prospectList.filter((p) => !p.isSample);

    const contactsForLookup = React.useMemo(() => {
        const seen = new Set<string>();
        const out: FaxContact[] = [];
        const add = (c: FaxContact) => {
            const k = (c.faxNumber || '').replace(/\D/g, '') || c.id;
            if (seen.has(k)) return;
            seen.add(k);
            out.push(c);
        };
        contacts.forEach(add);
        recipientSearchHits.forEach(add);
        return out;
    }, [contacts, recipientSearchHits]);

    const matchedContact = recipientValue ? findFaxContactByNumber(contactsForLookup, recipientValue) : undefined;
    const showSaveAsContact = Boolean(recipientValue?.trim()) && !matchedContact;

    const resetAndClose = () => {
        form.resetFields();
        setFaxStep(1);
        setFileList([]);
        setAttachmentMode('files');
        setAttachedDocuments([]);
        setShowDocumentsModal(false);
        setSelectedDocuments([]);
        setSelectedTemplateId(null);
        setSelectedDocumoUuid(null);
        setBuildFromScratch(false);
        setCoverTemplates([]);
        setDocumoTemplates([]);
        setRecipientSearchHits([]);
        setSelectedContactMeta(null);
        onClose();
    };

    const fetchCoverTemplates = async () => {
        setCoverTemplatesLoading(true);
        try {
            const result = await faxCoverPageTemplateList({ current: 1, size: 100 });
            const body = result?.data as
                | {
                      code?: number;
                      data?: { list?: FaxCoverPageTemplateVo[]; records?: FaxCoverPageTemplateVo[] };
                  }
                | undefined;
            if (!result?.error && body && isFaxScheduleSuccess(body)) {
                setCoverTemplates(body.data?.list ?? body.data?.records ?? []);
            }
        } finally {
            setCoverTemplatesLoading(false);
        }
    };

    const fetchDocumoTemplates = async () => {
        try {
            const result = await faxDocumoList();
            const body = result?.data as { code?: number; data?: CoverPageResponse[] } | undefined;
            if (!result?.error && body && isFaxScheduleSuccess(body)) {
                setDocumoTemplates(body.data ?? []);
            }
        } catch {
            // non-critical
        }
    };

    const goToStep2 = async () => {
        try {
            await form.validateFields(['recipient']);
            const hasFiles = fileList.length > 0;
            const hasDocs = attachedDocuments.length > 0;
            if (!isIntakeFaxSource && !hasFiles && !hasDocs) {
                message.warning('Please upload a file');
                return;
            }
            await Promise.all([fetchCoverTemplates(), fetchDocumoTemplates()]);
            setFaxStep(2);
        } catch {
            /* validation errors shown by form */
        }
    };

    const applyDocumentsToAttachment = async (items: IGetDocumentItem[]) => {
        const pdfItems = items.filter(isPdfDocument);
        if (pdfItems.length === 0) {
            message.error('Only PDF files can be sent by fax.');
            return;
        }
        if (pdfItems.length < items.length) {
            message.warning(`${items.length - pdfItems.length} non-PDF file(s) were skipped.`);
        }
        try {
            setAttachmentMode('documents');
            setAttachedDocuments(pdfItems);
            setFileList([]);
            message.success(`${pdfItems.length} document(s) attached`);
            setShowDocumentsModal(false);
            setSelectedDocuments([]);
        } catch {
            message.error('Could not attach documents from Documents.');
        }
    };

    const handleDocumentsModalOk = async () => {
        if (selectedDocuments.length === 0) {
            message.warning('Select at least one document');
            return;
        }
        await applyDocumentsToAttachment(selectedDocuments);
    };

    const handleSend = async () => {
        try {
            const values = form.getFieldsValue(true);
            const hasFiles = fileList.length > 0;
            const hasDocs = attachedDocuments.length > 0;
            if (!isIntakeFaxSource && !hasFiles && !hasDocs) {
                message.warning('Please upload a file');
                return;
            }
            const allFiles =
                attachmentMode === 'files' ? (fileList.map((f) => f.originFileObj).filter(Boolean) as File[]) : [];
            const documentIds =
                attachmentMode === 'documents'
                    ? attachedDocuments.map((d) => Number((d as any).id)).filter((id) => Number.isFinite(id))
                    : [];

            const normalizedRecipient = normalizeFaxForApi(values.recipient || '');
            const recipientFaxPayload = recipientFaxForSendApi(values.recipient || '');
            const sendExtras =
                selectedContactMeta && faxNumbersLooselyEqual(selectedContactMeta.fax, values.recipient || '')
                    ? {
                          contactId: selectedContactMeta.id,
                          recipientName: findFaxContactByNumber(contactsForLookup, values.recipient || '')?.name,
                      }
                    : {};

            setSending(true);

            const sourceForSend = faxSendContext?.source ?? (attachmentMode === 'documents' ? 'DOCUMENT' : 'DIRECT');
            const sourceIdForSend = faxSendContext?.sourceId;
            const intakeBusinessTypeForSend =
                faxSendContext?.intakeBusinessType !== undefined ? faxSendContext.intakeBusinessType : null;

            let documentIdsForSend: number[] | undefined;
            if (sourceForSend === 'INTAKE') {
                documentIdsForSend = undefined;
            } else if (attachmentMode === 'documents' && documentIds.length > 0) {
                documentIdsForSend = documentIds;
            } else {
                documentIdsForSend = undefined;
            }

            const result = await faxSend({
                recipientFax: recipientFaxPayload,
                recipientEmail: values.recipientEmail || undefined,
                clientId: values.patientId ? Number(values.patientId) : undefined,
                ...sendExtras,
                coverPage: faxStep === 2,
                coverPageTemplateId: selectedTemplateId ?? undefined,
                subject: values.coverSubject || undefined,
                notes: values.coverRemarks || undefined,
                tag: values.fileType ? [values.fileType] : undefined,
                source: sourceForSend,
                sourceId: sourceIdForSend,
                intakeBusinessType: intakeBusinessTypeForSend ?? undefined,
                documentIds: documentIdsForSend,
                files: sourceForSend === 'INTAKE' ? undefined : attachmentMode === 'files' ? allFiles : undefined,
            });

            const respData = result?.data as { code?: number; message?: string } | undefined;
            if (result?.error || !respData || !isFaxScheduleSuccess(respData)) {
                message.error(
                    respData?.message ||
                        (typeof result?.error === 'string' ? result.error : null) ||
                        'Failed to send fax',
                );
                return;
            }

            // Save as contact if requested
            if (values.saveAsContact && values.contactName?.trim() && normalizedRecipient) {
                try {
                    const createResult = await faxContactCreate({
                        faxNumber: normalizedRecipient,
                        name: values.contactName.trim(),
                        isFavorite: false,
                    });
                    const createEnvelope = createResult?.data as { code?: number; message?: string } | undefined;
                    if (!createResult?.error && createEnvelope && isFaxScheduleSuccess(createEnvelope)) {
                        onContactsChanged?.();
                    }
                } catch {
                    // non-critical — fax already sent
                }
            }

            message.success('Fax sent successfully!');
            onSendSuccess?.();
            resetAndClose();
        } catch {
            // validation errors handled by form
        } finally {
            setSending(false);
        }
    };

    const handlePrimaryClick = async () => {
        const wantsCover = includeCoverSheet !== false;
        if (faxStep === 2) {
            await handleSend();
        } else if (wantsCover) {
            await goToStep2();
        } else {
            try {
                await form.validateFields(['recipient']);
                const hasFiles = fileList.length > 0;
                const hasDocs = attachedDocuments.length > 0;
                if (!isIntakeFaxSource && !hasFiles && !hasDocs) {
                    message.warning('Please upload a file');
                    return;
                }
                await handleSend();
            } catch {
                /* validation shown */
            }
        }
    };

    const wantsCover = includeCoverSheet !== false;
    const hasRecipient = Boolean((recipientValue ?? '').trim?.());
    const hasAttachment = fileList.length > 0 || attachedDocuments.length > 0 || isIntakeFaxSource;

    let primaryLabel: string;
    let primaryDisabled: boolean;
    if (faxStep === 2) {
        primaryLabel = 'Send fax';
        primaryDisabled = !selectedTemplateId && !selectedDocumoUuid;
    } else {
        primaryLabel = wantsCover ? 'Continue' : 'Send fax';
        primaryDisabled = !hasRecipient || !hasAttachment;
    }

    const recipientSuggestionContacts = React.useMemo(
        () => mergeRecipientContactSuggestions(recipientValue || '', contacts, recipientSearchHits),
        [recipientValue, contacts, recipientSearchHits],
    );

    const contactOptions = recipientSuggestionContacts.map((c) => ({
        label: (
            <div className={s.contactOption}>
                <span className={s.contactOptionName}>{c.name}</span>
                <span className={s.contactOptionFax}>
                    {c.faxNumberDisplay || formatFaxNumber(c.faxNumber) || c.faxNumber}
                </span>
            </div>
        ),
        value: c.faxNumber,
        searchText: `${c.name} ${c.faxNumberDisplay || c.faxNumber}`,
        serverContactId: isFaxContactServerId(c.id) ? Number(c.id) : undefined,
    }));

    const showUploadFromDocuments = Boolean(patientId) && hasDocuments;

    // ── helpers (defined before use to avoid TDZ) ───────────────────────────

    const resetAttachments = () => {
        setFileList([]);
        setAttachedDocuments([]);
        setAttachmentMode('files');
        setShowDocumentsModal(false);
        setSelectedDocuments([]);
    };

    /** Get preview URL for a Documo template UUID */
    const getDocumoThumb = (uuid: string | undefined) => {
        if (!uuid) return undefined;
        const doc = documoTemplates.find((d) => d.uuid === uuid);
        return doc?.previewUrl || doc?.previewLink;
    };

    /** Selected Documo template (from strip or dropdown) */
    const selectedDocumoTpl = selectedDocumoUuid
        ? documoTemplates.find((d) => d.uuid === selectedDocumoUuid)
        : undefined;
    const selectedThumb = selectedDocumoTpl?.previewUrl || selectedDocumoTpl?.previewLink;
    const selectedTemplateHtml =
        typeof selectedDocumoTpl?.template === 'string' ? selectedDocumoTpl.template : undefined;
    const previewHtmlDoc = React.useMemo(() => {
        if (!selectedTemplateHtml) return undefined;
        return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <style>
      html, body { margin: 0; padding: 0; background: #fff; }
      /* Render as an A4-ish scaled preview */
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif; }
      .__previewRoot { transform: scale(0.72); transform-origin: top left; width: 138%; }
      img { max-width: 100%; height: auto; }
    </style>
  </head>
  <body><div class="__previewRoot">${selectedTemplateHtml}</div></body>
</html>`;
    }, [selectedTemplateHtml]);

    /** Select a template from the dropdown (by numeric saved-template ID) */
    const handleTemplateSelect = (templateId: number | null) => {
        if (!templateId) {
            setSelectedTemplateId(null);
            setSelectedDocumoUuid(null);
            setBuildFromScratch(false);
            form.setFieldsValue({ coverSubject: undefined, coverRemarks: undefined });
            return;
        }
        setSelectedTemplateId(templateId);
        setBuildFromScratch(false);
        const tpl = coverTemplates.find((t) => t.id === templateId);
        setSelectedDocumoUuid(tpl?.documoCoverpageId || null);
        form.setFieldsValue({
            coverSubject: tpl?.subject ?? undefined,
            coverRemarks: tpl?.note ?? undefined,
        });
    };

    /** Select a Documo template from the thumbnail strip (by UUID) */
    const handleDocumoTemplateSelect = (uuid: string) => {
        setSelectedDocumoUuid(uuid);
        // Keep scratch panel open — do not set buildFromScratch false here
        // Try to match a saved template so subject/notes can be pre-filled
        // and so coverPageTemplateId is available when sending
        const matchedTpl = coverTemplates.find((t) => t.documoCoverpageId === uuid);
        setSelectedTemplateId(matchedTpl?.id ?? null);
        form.setFieldsValue({
            coverSubject: matchedTpl?.subject ?? undefined,
            coverRemarks: matchedTpl?.note ?? undefined,
        });
    };

    const handleBuildFromScratch = () => {
        setBuildFromScratch(true);
        setSelectedTemplateId(null);
        setSelectedDocumoUuid(null);
        form.setFieldsValue({ coverSubject: undefined, coverRemarks: undefined });
    };

    /**
     * Show preview as soon as a template is selected.
     * “Build from scratch” keeps the template strip visible, but preview should also show
     * when user picks a saved cover sheet template from the dropdown.
     */
    const showPreviewCard = buildFromScratch || Boolean(selectedTemplateId) || Boolean(selectedDocumoUuid);

    const removeFile = (uid: string) => setFileList((prev) => prev.filter((x) => x.uid !== uid));
    const removeDocument = (id: number) =>
        setAttachedDocuments((prev) => prev.filter((x) => Number((x as any).id) !== id));

    const replaceFile = (uid: string, file: File) => {
        setFileList((prev) =>
            prev.map((x) =>
                x.uid === uid
                    ? {
                          uid: file.uid || uid,
                          name: file.name,
                          status: 'done',
                          originFileObj: file as unknown as UploadFile['originFileObj'],
                      }
                    : x,
            ),
        );
    };

    return (
        <>
            <Modal
                open={open}
                footer={null}
                closable={false}
                width={543}
                centered
                destroyOnClose
                zIndex={1100}
                styles={{ body: { padding: 0 } }}
                wrapClassName={s.sendFaxModalWrap}
                onCancel={resetAndClose}
            >
                {/* Header */}
                <div className={s.modalHeader}>
                    <span className={s.modalTitle}>Send fax</span>
                    <button type="button" className={s.modalClose} onClick={resetAndClose} aria-label="Close">
                        <CloseOutlined />
                    </button>
                </div>

                {/* Scrollable content */}
                <div className={s.modalContent}>
                    <Spin spinning={sending}>
                        <Form
                            form={form}
                            layout="vertical"
                            requiredMark={false}
                            initialValues={{ includeCoverSheet: true }}
                            className={s.form}
                        >
                            {faxStep === 2 ? (
                                /* ── Step 2: Cover sheet ── */
                                <>
                                    <button
                                        type="button"
                                        className={s.backLink}
                                        onClick={() => {
                                            setFaxStep(1);
                                            setBuildFromScratch(false);
                                        }}
                                    >
                                        <ArrowLeftOutlined />
                                        Back
                                    </button>

                                    <Spin spinning={coverTemplatesLoading}>
                                        <Form.Item label="Apply a cover sheet template">
                                            <Select
                                                allowClear
                                                showSearch
                                                placeholder="Select a cover sheet template"
                                                optionFilterProp="label"
                                                value={selectedTemplateId}
                                                onChange={(val) => handleTemplateSelect(val ?? null)}
                                                options={coverTemplates.map((t) => ({
                                                    label: t.templateName || String(t.id),
                                                    value: t.id,
                                                    subject: t.subject,
                                                    thumbUrl: t.previewLink || getDocumoThumb(t.documoCoverpageId),
                                                }))}
                                                optionRender={(option) => (
                                                    <div className={s.templateDropdownOption}>
                                                        <div className={s.templateDropdownThumb}>
                                                            {(option.data as any).thumbUrl ? (
                                                                <img src={(option.data as any).thumbUrl} alt="" />
                                                            ) : (
                                                                <div className={s.templateDropdownThumbPlaceholder}>
                                                                    <FileTextOutlined />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className={s.templateDropdownMeta}>
                                                            <span className={s.templateDropdownName}>
                                                                {option.label}
                                                            </span>
                                                            {(option.data as any).subject && (
                                                                <span className={s.templateDropdownSubject}>
                                                                    {(option.data as any).subject}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            />
                                        </Form.Item>
                                    </Spin>

                                    <button
                                        type="button"
                                        className={s.buildFromScratch}
                                        onClick={handleBuildFromScratch}
                                    >
                                        Build from scratch
                                    </button>

                                    {buildFromScratch && documoTemplates.length > 0 && (
                                        <div className={s.templateStrip}>
                                            {documoTemplates.map((tpl) => {
                                                const thumb = tpl.previewUrl || tpl.previewLink;
                                                const isSelected = selectedDocumoUuid === tpl.uuid;
                                                return (
                                                    <div
                                                        key={tpl.uuid}
                                                        className={s.templateThumbWrapper}
                                                        onClick={() => handleDocumoTemplateSelect(tpl.uuid!)}
                                                    >
                                                        <div
                                                            className={`${s.templateThumbCard} ${isSelected ? s.templateThumbCardSelected : ''}`}
                                                        >
                                                            {thumb ? (
                                                                <img
                                                                    src={thumb}
                                                                    alt={tpl.name}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover',
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className={s.templateThumbPlaceholder}>
                                                                    <FileTextOutlined />
                                                                    <span>{tpl.name}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className={s.templateThumbName}>{tpl.name}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {showPreviewCard && (
                                        <div className={s.a4PreviewCard}>
                                            {/* Template preview */}
                                            <div className={s.a4PreviewImage}>
                                                {previewHtmlDoc ? (
                                                    <iframe
                                                        title="Template preview"
                                                        className={s.a4PreviewIframe}
                                                        srcDoc={previewHtmlDoc}
                                                        sandbox=""
                                                        referrerPolicy="no-referrer"
                                                    />
                                                ) : selectedThumb ? (
                                                    <img src={selectedThumb} alt="" className={s.a4PreviewImg} />
                                                ) : (
                                                    <div className={s.a4PreviewImagePlaceholder}>
                                                        <FileTextOutlined />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Editable fields */}
                                            <div className={s.a4PreviewFields}>
                                                <Form.Item
                                                    label="Subject"
                                                    name="coverSubject"
                                                    style={{ marginBottom: 8 }}
                                                >
                                                    <Input placeholder="Subject here" />
                                                </Form.Item>
                                                <Form.Item
                                                    label="Remarks"
                                                    name="coverRemarks"
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <RichTextArea placeholder="Remarks here" />
                                                </Form.Item>
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* ── Step 1: Compose ── */
                                <>
                                    {/* 1. Recipient */}
                                    <Form.Item
                                        label="Recipient"
                                        name="recipient"
                                        validateTrigger={['onBlur', 'onSubmit']}
                                        rules={[
                                            { required: true, message: 'Please enter a fax number' },
                                            {
                                                validator: async (_, value) => {
                                                    const raw = (value || '').trim();
                                                    if (!raw) return;
                                                    const digits = raw.replace(/\D/g, '');
                                                    if (digits.length < 10) {
                                                        throw new Error(
                                                            'Enter a valid fax number (at least 10 digits)',
                                                        );
                                                    }
                                                },
                                            },
                                        ]}
                                    >
                                        <AutoComplete
                                            allowClear
                                            options={contactOptions}
                                            filterOption={false}
                                            notFoundContent={recipientSearchLoading ? <Spin size="small" /> : null}
                                            onSelect={(faxVal, option) => {
                                                const norm = faxNanpDigitsForInput(String(faxVal));
                                                form.setFieldValue('recipient', norm);
                                                const id = (option as { serverContactId?: number }).serverContactId;
                                                if (id != null && Number.isFinite(id)) {
                                                    setSelectedContactMeta({ id, fax: String(faxVal) });
                                                } else {
                                                    setSelectedContactMeta(null);
                                                }
                                            }}
                                        >
                                            <EFaxRecipientQueryInput
                                                placeholder="Search contacts or enter fax number manually"
                                                size="large"
                                            />
                                        </AutoComplete>
                                    </Form.Item>

                                    {/* 2. Save as contact (conditional) */}
                                    {showSaveAsContact && (
                                        <div className={s.saveAsContactRow}>
                                            <Form.Item name="saveAsContact" valuePropName="checked">
                                                <Checkbox>Save as contact</Checkbox>
                                            </Form.Item>
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prev, cur) => prev.saveAsContact !== cur.saveAsContact}
                                            >
                                                {({ getFieldValue }) =>
                                                    getFieldValue('saveAsContact') ? (
                                                        <Form.Item name="contactName">
                                                            <Input placeholder="Add name for the contact" />
                                                        </Form.Item>
                                                    ) : null
                                                }
                                            </Form.Item>
                                        </div>
                                    )}

                                    {/* 3. Patient (Optional) */}
                                    <Form.Item label="Patient (Optional)" name="patientId">
                                        <Select
                                            allowClear
                                            showSearch
                                            loading={prospectListLoading}
                                            placeholder="Select patient"
                                            optionFilterProp="label"
                                            onChange={() => {
                                                // When switching patient, clear any previously selected/uploaded files.
                                                resetAttachments();
                                            }}
                                            options={clientOptions.map((p) => ({
                                                label: `${p.firstName} ${p.lastName}`,
                                                value: p.id,
                                            }))}
                                        />
                                    </Form.Item>

                                    {/* 4. Files — INTAKE: server builds PDF from intake record */}
                                    <Form.Item label={isIntakeFaxSource ? 'Attachment' : 'Files'}>
                                        <Spin spinning={documentsLoading}>
                                            {isIntakeFaxSource ? (
                                                <div className={s.infoAlert}>
                                                    <InfoCircleFilled className={s.infoAlertIcon} />
                                                    <div className={s.infoAlertContent}>
                                                        <div className={s.infoAlertText}>
                                                            This fax will include the intake form as a PDF (generated
                                                            automatically). Sending may take up to 20–30 minutes.
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {showInfo && (
                                                        <div className={s.infoAlert}>
                                                            <InfoCircleFilled className={s.infoAlertIcon} />
                                                            <div className={s.infoAlertContent}>
                                                                <div className={s.infoAlertText}>
                                                                    Only PDF documents can be sent via eFax. Files must
                                                                    be 100 MB or smaller. Sending or receiving a fax may
                                                                    take up to 20–30 minutes.
                                                                </div>
                                                                <span
                                                                    className={s.infoAlertDismiss}
                                                                    onClick={() => setShowInfo(false)}
                                                                >
                                                                    Dismiss
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* File card list */}
                                                    {attachmentMode === 'files' && fileList.length > 0 && (
                                                        <div className={s.fileCardList}>
                                                            {fileList.map((f) => (
                                                                <div key={f.uid} className={s.fileRow}>
                                                                    <div className={s.fileCard}>
                                                                        <div className={s.fileCardName}>{f.name}</div>
                                                                        <div className={s.fileCardType}>PDF</div>
                                                                    </div>

                                                                    {/* Change button (beside card) */}
                                                                    <Upload
                                                                        showUploadList={false}
                                                                        beforeUpload={(file) =>
                                                                            validateAndAddFile(file, (uf) =>
                                                                                replaceFile(
                                                                                    f.uid,
                                                                                    uf.originFileObj as File,
                                                                                ),
                                                                            )
                                                                        }
                                                                        accept=".pdf"
                                                                    >
                                                                        <Button className={s.changeBtn}>Change</Button>
                                                                    </Upload>

                                                                    {/* X-circle — absolute on fileRow */}
                                                                    <button
                                                                        type="button"
                                                                        className={s.fileCardRemove}
                                                                        onClick={() => removeFile(f.uid)}
                                                                        aria-label="Remove file"
                                                                    >
                                                                        <CloseCircleFilled />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Documents list (attached by documentIds) */}
                                                    {attachmentMode === 'documents' && attachedDocuments.length > 0 && (
                                                        <div className={s.fileCardList}>
                                                            {attachedDocuments.map((d) => {
                                                                const id = Number((d as any).id);
                                                                const name =
                                                                    d.fileOriginName || d.name || `Document #${id}`;
                                                                return (
                                                                    <div key={`doc-${id}`} className={s.fileRow}>
                                                                        <div className={s.fileCard}>
                                                                            <div className={s.fileCardName}>{name}</div>
                                                                            <div className={s.fileCardType}>PDF</div>
                                                                        </div>

                                                                        <Button
                                                                            className={s.changeBtn}
                                                                            onClick={() => {
                                                                                setSelectedDocuments([]);
                                                                                setShowDocumentsModal(true);
                                                                            }}
                                                                        >
                                                                            Change
                                                                        </Button>

                                                                        <button
                                                                            type="button"
                                                                            className={s.fileCardRemove}
                                                                            onClick={() => {
                                                                                if (Number.isFinite(id))
                                                                                    removeDocument(id);
                                                                            }}
                                                                            aria-label="Remove document"
                                                                        >
                                                                            <CloseCircleFilled />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* Upload button */}
                                                    <div className={s.uploadBtnWrap}>
                                                        <Upload
                                                            ref={uploadRef}
                                                            fileList={[]}
                                                            beforeUpload={(file) =>
                                                                validateAndAddFile(file, (uf) =>
                                                                    setFileList((prev) => {
                                                                        setAttachmentMode('files');
                                                                        setAttachedDocuments([]);
                                                                        return [...prev, uf];
                                                                    }),
                                                                )
                                                            }
                                                            maxCount={5}
                                                            accept=".pdf"
                                                            openFileDialogOnClick={false}
                                                        >
                                                            <Dropdown
                                                                menu={{
                                                                    items: [
                                                                        { key: 'device', label: 'Upload from device' },
                                                                        ...(showUploadFromDocuments
                                                                            ? [
                                                                                  {
                                                                                      key: 'documents',
                                                                                      label: 'Upload from "Documents"',
                                                                                  },
                                                                              ]
                                                                            : []),
                                                                    ],
                                                                    onClick: ({ key }) => {
                                                                        if (key === 'device') {
                                                                            (
                                                                                uploadRef.current as any
                                                                            )?.upload?.uploader?.fileInput?.click();
                                                                        } else if (key === 'documents') {
                                                                            setSelectedDocuments([]);
                                                                            setShowDocumentsModal(true);
                                                                        }
                                                                    },
                                                                }}
                                                                trigger={['click']}
                                                            >
                                                                <Button icon={<UploadOutlined />}>
                                                                    Upload files <DownOutlined />
                                                                </Button>
                                                            </Dropdown>
                                                        </Upload>
                                                    </div>
                                                </>
                                            )}
                                        </Spin>
                                    </Form.Item>

                                    {/* 5. Recipient email (Optional) */}
                                    <Form.Item label="Recipient email (Optional)" name="recipientEmail">
                                        <Input placeholder="Enter recipient email (optional)" />
                                    </Form.Item>

                                    {/* 6. File type (Optional) */}
                                    <Form.Item label="File type (Optional)" name="fileType">
                                        <Select
                                            allowClear
                                            placeholder="Select file type"
                                            options={FAX_TAG_OPTIONS.map((opt) => ({
                                                label: opt,
                                                value: opt,
                                            }))}
                                        />
                                    </Form.Item>

                                    {/* 7. Include cover sheet */}
                                    <Form.Item name="includeCoverSheet" valuePropName="checked">
                                        <Checkbox>Include cover sheet</Checkbox>
                                    </Form.Item>
                                </>
                            )}
                        </Form>
                    </Spin>
                </div>

                {/* Footer */}
                <div className={s.modalFooter}>
                    <div className={s.modalFooterActions}>
                        <Button onClick={resetAndClose}>Cancel</Button>
                        <Button
                            type="primary"
                            disabled={primaryDisabled}
                            onClick={handlePrimaryClick}
                            loading={sending}
                        >
                            {primaryLabel}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Select from Documents sub-modal */}
            <Modal
                title="Select files from Documents"
                open={showDocumentsModal}
                onCancel={() => {
                    setShowDocumentsModal(false);
                    setSelectedDocuments([]);
                }}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 12 }}>
                        <Button type="primary" onClick={handleDocumentsModalOk}>
                            Confirm
                        </Button>
                        <Button
                            onClick={() => {
                                setShowDocumentsModal(false);
                                setSelectedDocuments([]);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                }
                centered
                width={800}
                destroyOnClose
                styles={{ body: { padding: 0 } }}
            >
                <div style={{ maxHeight: 'calc(80vh - 120px)', overflowY: 'auto' }}>
                    <DocumentsTable
                        clientId={patientId}
                        showUploadButton={false}
                        selectable
                        selectionType="checkbox"
                        includeAllDocuments
                        pdfOnly
                        listPageSize={50}
                        hideEdit
                        onSelectRows={setSelectedDocuments}
                    />
                </div>
            </Modal>
        </>
    );
};

export default SendFaxModal;
